import { useMemo } from 'react'
import { ContentfulLivePreview } from '@contentful/live-preview'

import { Container } from '../../components/container'
import Bar from '../../components/icons/bar'
import HalfCircle from '../../components/icons/half-circle'
import HalfPie from '../../components/icons/half-pie'
import People from '../../components/icons/people'
import Pie99 from '../../components/icons/pie-99'
import Ribbon from '../../components/icons/ribbon'
import Trophy from '../../components/icons/trophy'
import { useBreakpoints } from '../../hooks/use-breakpoints'
import { CmsColor } from '../../models/cms-color'
import { cn } from '../../utils/cn'
import { RichText } from '../helper-components/rich-text'

interface StatsCmsProps {
  id: string
  isH1?: boolean
  title: any | string
  alignment: string
  iconChoiceStat1: string
  stat1Subtitle: any | string
  stat1Text: string
  iconChoiceStat2: string
  stat2Subtitle: any | string
  stat2Text: string
  iconChoiceStat3: string
  stat3Subtitle: any | string
  stat3Text: string
  color?: CmsColor
  padding?: boolean
  titleTextSize?: string
}

const Stat = (props: {
  id: string
  iconChoice: string
  statText: string
  statSubtitle: any | string
  fieldName: string
  className?: string
  color?: CmsColor
}) => {
  const icon = useMemo(() => {
    switch (props.iconChoice) {
      case 'pie99':
        return <Pie99 />
      case 'bar':
        return <Bar />
      case 'halfCircle':
        return <HalfCircle />
      case 'halfPie':
        return <HalfPie />
      case 'people':
        return <People />
      case 'ribbon':
        return <Ribbon height="85" width="85" />
      case 'trophy':
        return <Trophy />
      default:
        console.warn('unknown icon in stats bar: ' + props.iconChoice)
    }
  }, [props.iconChoice])

  const { isMobile } = useBreakpoints()
  return (
    <div
      className={cn(props.className, 'flex xl:items-center gap-6 self-stretch')}
    >
      <div
        {...ContentfulLivePreview.getProps({
          entryId: props.id,
          fieldId: 'iconChoice' + props.fieldName,
          locale: 'en-US',
        })}
        className="shrink-0 items-start xl:items-center pt-4 xl:pt-0 sm:w-[74px]"
        style={
          isMobile ? { width: '74px' } : { width: 'clamp(48px, 6vw, 80px)' }
        }
      >
        {icon}
      </div>
      <div className="self-start">
        <div
          className={cn(
            'font-serif text-3xl font-semibold leading-[3.3rem] text-neutral-900 lg:text-[2.8rem]'
          )}
          style={{
            color: props.color?.textColor,
          }}
          {...ContentfulLivePreview.getProps({
            entryId: props.id,
            fieldId: props.fieldName + 'Text',
            locale: 'en-US',
          })}
        >
          {props.statText}
        </div>
        <RichText
          className={cn(
            'text-md md:text-base ',
            props.color?.textColor == null
              ? ''
              : `text-color-[${props.color.textColor}]`
          )}
          json={props.statSubtitle?.json}
          links={props.statSubtitle?.links}
          {...ContentfulLivePreview.getProps({
            entryId: props.id,
            fieldId: props.fieldName + 'Subtitle',
            locale: 'en-US',
          })}
        />
      </div>
    </div>
  )
}

const StatsBannerCMS = (props: StatsCmsProps) => {
  return (
    <div
      id={props.id}
      className={cn(
        'bg-background scroll-mt-25',
        props.iconChoiceStat3 === null ? 'lg:flex-row' : 'lg:grid-cols-2',
        props.alignment === 'Left' ? '' : 'lg:flex-row-reverse'
      )}
      style={{
        backgroundColor: props.color?.backgroundColor,
      }}
    >
      <Container>
        <div
          className={cn(
            'flex py-9 items-center justify-center lg:justify-between',
            props.iconChoiceStat3 ? 'flex-col' : 'flex-col lg:flex-row',
            props.padding && 'mx-auto p-4 sm:p-16'
          )}
        >
          {props.title && (
            <h2
              aria-level={props.isH1 ? 1 : 2}
              className={cn(
                'flex-1 pb-13 md:pb-20',
                props.iconChoiceStat3
                  ? 'text-center'
                  : 'text-center lg:text-start'
              )}
            >
              <RichText
                className={cn(
                  'font-sans text-3xl tracking-wide text-neutral-900 font-normal leading-[3.6rem]',

                  props.color?.textColor == null
                    ? ''
                    : `text-color-[${props.color.textColor}]`,
                  props.titleTextSize === 'Small' ? 'text-3xl' : 'text-5xl'
                )}
                json={props.title?.json}
                links={props.title?.links}
                {...ContentfulLivePreview.getProps({
                  entryId: props.id,
                  fieldId: 'title',
                  locale: 'en-US',
                })}
              />
            </h2>
          )}
          <div
            className="flex flex-[2] flex-wrap flex-col sm:flex-row lg:items-center gap-10"
            style={{
              color: props.color?.textColor,
            }}
          >
            <Stat
              id={props.id}
              iconChoice={props.iconChoiceStat1}
              statText={props.stat1Text}
              statSubtitle={props.stat1Subtitle}
              fieldName="stat1"
              className="flex-1"
              color={props.color}
            />
            <Stat
              id={props.id}
              iconChoice={props.iconChoiceStat2}
              statText={props.stat2Text}
              statSubtitle={props.stat2Subtitle}
              fieldName="stat2"
              className="flex-1"
              color={props.color}
            />
            {props.stat3Text === null ? (
              ''
            ) : (
              <Stat
                id={props.id}
                iconChoice={props.iconChoiceStat3}
                statText={props.stat3Text}
                statSubtitle={props.stat3Subtitle}
                fieldName="stat3"
                className="flex-1"
                color={props.color}
              />
            )}
          </div>
        </div>
      </Container>
    </div>
  )
}

export { StatsBannerCMS }
